import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PageBanner from "@/components/PageBanner";
import PaymentPartners from "@/components/PaymentPartners";
import { FaPlaneDeparture } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import { Phone, User } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import { trackWhatsAppClick } from "@/lib/analytics";

const flightInfo = {
  requirements: [
    "Choose Your Destination and Dates",
    "Contact Us with your travel requirements",
    "Select Your Flight based on schedule and budget",
    "Provide Passenger Information",
    "Make the Payment",
    "Receive Confirmation",
  ],
  details: [
    "Flight numbers and departure/arrival times",
    "Terminal information and check-in counters",
    "Baggage allowances and restrictions",
    "In-flight amenities and services",
  ],
  afterBooking: [
    "Check in online 24-48 hours before departure",
    "Arrive early at the airport",
    "Check in and drop off baggage",
    "Go through security",
    "Proceed to boarding gate",
  ],
};

const airlines = [
  {
    name: "Uganda Airlines",
    image: "/AIR-01.png",
  },
  {
    name: "Kenya Airways",
    image: "/AIR-02.png",
  },
  {
    name: "RwandAir",
    image: "/AIR-03.png",
  },
  {
    name: "Air Tanzania",
    image: "/AIR-04.png",
  },
  {
    name: "Turkish Airlines",
    image: "/AIR-05.png",
  },
  {
    name: "Qatar Airways",
    image: "/AIR-06.png",
  },
  {
    name: "Brussels Airlines",
    image: "/AIR-07.png",
  },
  {
    name: "Ethiopian Airlines",
    image: "/AIR-08.png",
  },
  {
    name: "British Airways",
    image: "/AIR-09.png",
  },
  {
    name: "KLM Royal Dutch Airlines",
    image: "/AIR-10.png",
  },
  {
    name: "Emirates",
    image: "/AIR-11.png",
  },
  {
    name: "South African Airways",
    image: "/AIR-12.png",
  },
  {
    name: "Egypt Air",
    image: "/AIR-13.png",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
  },
};

export default function AirTicketing() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{t("airTicketing")} | Ecoplace Tours</title>
        <meta
          name="description"
          content="Book your flights with Ecoplace Tours. We offer competitive rates and handle all your air travel needs."
        />
      </Helmet>

      <PageBanner
        title={t("airTicketing")}
        description="Book your flights with ease. We handle domestic and international flight reservations at competitive rates."
        backgroundImage="/ugair.png"
      />

      <div className="container mx-auto px-4 py-8 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-700">
            {/* <span className="font-semibold text-green-700">Let us handle your flight arrangements</span> for a seamless travel experience. We offer competitive rates on domestic and international flights, with access to special fares and a dedicated support team to assist you every step of the way. */}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button
              onClick={() => {
                trackWhatsAppClick('Air Booking Hero');
                window.open("https://wa.me/256780628299?text=I%20want%20to%20book%20a%20flight", "_blank");
              }}
              className="bg-[#25D366] hover:bg-[#128C7E] text-white text-lg px-8 py-6 h-auto shadow-lg hover:shadow-xl transition-all"
            >
              <Phone className="w-5 h-5 mr-2" />
              Book Flights via WhatsApp
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                trackWhatsAppClick('Air Partner Fares');
                window.open("https://wa.me/256780628299?text=I%20want%20to%20inquire%20about%20partner%20fares", "_blank");
              }}
              className="bg-white border-primary text-primary hover:bg-white/90 font-semibold shadow-md hover:shadow-lg transition-all text-lg px-8 py-6 h-auto"
            >
              <FaPlaneDeparture className="w-5 h-5 mr-2" />
              Get Partner Fares
            </Button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Card className="h-full bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">
                  Booking Process
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {flightInfo.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="font-bold text-primary text-lg">
                        {index + 1}.
                      </span>
                      <span className="text-gray-700">
                        {req.split(" ").map((word, i) =>
                          i === 0 ? (
                            <span key={i} className="font-semibold">
                              {word}{" "}
                            </span>
                          ) : (
                            word + " "
                          ),
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Card className="h-full bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">
                  Flight Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {flightInfo.details.map((detail, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary text-lg">•</span>
                      <span className="text-gray-700">
                        {detail.split(" ").map((word, i) =>
                          i === 0 ? (
                            <span key={i} className="font-semibold">
                              {word}{" "}
                            </span>
                          ) : (
                            word + " "
                          ),
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Card className="h-full bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">
                  After Booking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {flightInfo.afterBooking.map((step, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary text-lg">•</span>
                      <span className="text-gray-700">
                        {step.split(" ").map((word, i) =>
                          i === 0 ? (
                            <span key={i} className="font-semibold">
                              {word}{" "}
                            </span>
                          ) : (
                            word + " "
                          ),
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-2 font-heading">
            Our Partner Airlines
          </h2>
          <div className="w-24 h-1 bg-green-600 mx-auto rounded-full mb-4"></div>
          <p className="mt-4 text-lg text-gray-700 max-w-3xl mx-auto">
            We work with leading airlines to provide you with the best flight
            options for your journey.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
        >
          {airlines.map((airline, index) => (
            <motion.div
              key={airline.name}
              variants={itemVariants}
              className="relative group flex flex-col items-center justify-center p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 bg-white hover:bg-gray-50 overflow-hidden"
            >
              <div className="h-20 w-full flex items-center justify-center">
                <img
                  src={airline.image}
                  alt={`${airline.name} Logo`}
                  className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Overlay with airline name on hover */}
              <div className="absolute inset-0 bg-black/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-white text-center font-semibold text-sm px-2">
                  {airline.name}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 mb-16"
        >
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold font-heading text-center mb-6">
              Contact Our Air Ticketing Specialists
            </h2>
            <p className="text-lg text-center text-gray-700 max-w-3xl mx-auto mb-8">
              Our dedicated specialists are ready to assist you with all your
              flight booking needs. Reach out directly for personalized service.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                onClick={() => {
                  trackWhatsAppClick('Air Booking Specialist');
                  window.open("https://wa.me/256780628299?text=I%20want%20to%20book%20a%20flight", "_blank");
                }}
                className="bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold shadow-md hover:shadow-lg transition-all"
              >
                <Phone className="w-4 h-4 mr-2" />
                Book via WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  trackWhatsAppClick('Air Partner Specialist');
                  window.open("https://wa.me/256780628299?text=I%20want%20to%20inquire%20about%20partner%20fares", "_blank");
                }}
                className="bg-white border-primary text-primary hover:bg-white/90 font-semibold shadow-md hover:shadow-lg transition-all"
              >
                <FaPlaneDeparture className="w-4 h-4 mr-2" />
                Get Partner Fares
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow transform hover:-translate-y-1 duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <User className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-2 font-heading">
                    Bella
                  </h3>
                  <p className="text-gray-700 mb-4 text-lg">
                    Air Ticketing Specialist
                  </p>
                  <a
                    href="tel:+256789254266"
                    className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-full hover:bg-primary/90 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    +256 789 254266
                  </a>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow transform hover:-translate-y-1 duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <User className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-2 font-heading">
                    Peter
                  </h3>
                  <p className="text-gray-700 mb-4 text-lg">
                    Air Ticketing Specialist
                  </p>
                  <a
                    href="tel:+256774498775"
                    className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-full hover:bg-primary/90 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    +256 774 498775
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <PaymentPartners />
    </div >
  );
}
